package com.schoolconnect.app.repository.notices

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.NoticeCreateRequest
import com.schoolconnect.app.datasource.network.request.NoticeUpdateRequest
import com.schoolconnect.app.datasource.network.response.NoticeResponse
import com.schoolconnect.app.datasource.network.response.TeacherNoticeStatisticsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog
import kotlinx.serialization.json.JsonObject
import kotlinx.coroutines.CoroutineScope
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey

data class NoticesPageResult(
    val items: List<NoticeResponse>,
    val totalCount: Int
)

object NoticesListRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] NoticesListRepository"
    private val pageCache = RepositoryCache<NoticesPageResult>()

    suspend fun create(
        request: NoticeCreateRequest,
        currentUserId: String? = null
    ): Result<NoticeResponse> = KtorApiClient.createNotice(request, currentUserId).onSuccess { pageCache.clear() }

    suspend fun update(noticeId: String, request: NoticeUpdateRequest): Result<NoticeResponse> = KtorApiClient.updateNotice(noticeId, request).onSuccess { pageCache.clear() }

    suspend fun delete(noticeId: String): Result<JsonObject> = KtorApiClient.deleteNotice(noticeId).onSuccess { pageCache.clear() }

    suspend fun loadPage(
        search: String? = null,
        audience: String? = null,
        status: String? = null,
        publishedFrom: String? = null,
        publishedTo: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String? = null,
        order: String? = null,
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope
    ): Result<NoticesPageResult> {
        val safePageSize = pageSize.coerceIn(1, 100)
        val safeOffset = pageIndex.coerceAtLeast(0) * safePageSize
        val key = tenantCacheKey("notices:${search.orEmpty()}:${audience.orEmpty()}:${status.orEmpty()}:${publishedFrom.orEmpty()}:${publishedTo.orEmpty()}:$safeOffset:$safePageSize:${sortBy.orEmpty()}:${order.orEmpty()}")
        return pageCache.load(refreshScope, TAG, "loadPage", key, forceRemote = forceRemote) {
            KtorApiClient.getNotices(
                noticeId = search,
                audience = audience,
                status = status,
                publishedFrom = publishedFrom,
                publishedTo = publishedTo,
                limit = safePageSize,
                offset = safeOffset,
                sortBy = sortBy,
                order = order
            ).map { rows -> NoticesPageResult(rows, rows.serverTotalCountOrNull() ?: rows.size) }
                .onFailure { throwable -> scLog("$TAG loadPage failure pageIndex=$pageIndex", throwable) }
        }
    }

    suspend fun loadStatistics(): Result<TeacherNoticeStatisticsResponse> =
        KtorApiClient.getTeacherNoticeStatistics()
            .onFailure { throwable -> scLog("$TAG loadStatistics failure", throwable) }

    suspend fun loadDetail(noticeId: String): Result<NoticeResponse> =
        KtorApiClient.getNotice(noticeId)
            .onFailure { throwable -> scLog("$TAG loadDetail failure noticeId=$noticeId", throwable) }
}
